import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '@/context/TenantContext';
import { supabase } from '@/lib/supabase';

interface ChecklistStep {
  id: string;
  title: string;
  description: string;
  icon: string;
  cta: string;
  ctaPath: string;
  completedLabel: string;
}

const STEPS: ChecklistStep[] = [
  {
    id: 'whatsapp',
    title: 'Connect WhatsApp',
    description: 'Link your WhatsApp Business number to start receiving and sending messages from your workspace.',
    icon: 'ri-whatsapp-line',
    cta: 'Set Up WhatsApp',
    ctaPath: '/whatsapp',
    completedLabel: 'WhatsApp connected',
  },
  {
    id: 'first_lead',
    title: 'Add Your First Lead',
    description: 'Capture your first potential customer — manually or via your lead capture form.',
    icon: 'ri-user-add-line',
    cta: 'Add a Lead',
    ctaPath: '/crm/leads',
    completedLabel: 'First lead added',
  },
  {
    id: 'first_quote',
    title: 'Send Your First Quote',
    description: 'Build a professional travel quote and send it to a client in minutes.',
    icon: 'ri-file-text-line',
    cta: 'Create a Quote',
    ctaPath: '/quotes',
    completedLabel: 'First quote created',
  },
  {
    id: 'first_product',
    title: 'Add a Travel Product',
    description: 'Create your first package, tour, or service so you can attach it to quotes and bookings.',
    icon: 'ri-suitcase-2-line',
    cta: 'Add a Product',
    ctaPath: '/products',
    completedLabel: 'First product added',
  },
  {
    id: 'invite_team',
    title: 'Invite a Team Member',
    description: 'Add an agent or finance staff to collaborate on leads and bookings.',
    icon: 'ri-team-line',
    cta: 'Manage Staff',
    ctaPath: '/settings',
    completedLabel: 'Team member invited',
  },
];

const STORAGE_KEY_PREFIX = 'onboarding_checklist_';
const DISMISSED_KEY_PREFIX = 'onboarding_dismissed_';

function useOnboardingState(tenantId: string) {
  const storageKey = `${STORAGE_KEY_PREFIX}${tenantId}`;
  const dismissedKey = `${DISMISSED_KEY_PREFIX}${tenantId}`;

  const [completed, setCompleted] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });

  const [dismissed, setDismissed] = useState<boolean>(() => {
    return localStorage.getItem(dismissedKey) === 'true';
  });

  const markComplete = (stepId: string) => {
    setCompleted((prev) => {
      const next = new Set(prev);
      next.add(stepId);
      localStorage.setItem(storageKey, JSON.stringify([...next]));
      return next;
    });
  };

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(dismissedKey, 'true');
  };

  return { completed, markComplete, dismissed, dismiss };
}

export default function OnboardingChecklist() {
  const navigate = useNavigate();
  const { agencyProfile, currentUser } = useTenant();
  const tenantId = agencyProfile.id || currentUser.id || 'default';

  const { completed, markComplete, dismissed, dismiss } = useOnboardingState(tenantId);
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [celebrating, setCelebrating] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const completedCount = STEPS.filter((s) => completed.has(s.id)).length;
  const totalCount = STEPS.length;
  const progressPct = Math.round((completedCount / totalCount) * 100);
  const allDone = completedCount === totalCount;
  const brand = agencyProfile.brandColor || '#0D9488';

  // Auto-check real data from Supabase
  useEffect(() => {
    if (!agencyProfile.id) return;

    const checkRealData = async () => {
      const tenantId = agencyProfile.id;

      const [leadsRes, quotesRes, productsRes, profilesRes] = await Promise.all([
        supabase.from('leads').select('id').eq('tenant_id', tenantId).limit(1),
        supabase.from('quotes').select('id').eq('tenant_id', tenantId).limit(1),
        supabase.from('products').select('id').eq('tenant_id', tenantId).limit(1),
        supabase.from('profiles').select('id').eq('tenant_id', tenantId).limit(2),
      ]);

      if ((leadsRes.data?.length ?? 0) > 0) markComplete('first_lead');
      if ((quotesRes.data?.length ?? 0) > 0) markComplete('first_quote');
      if ((productsRes.data?.length ?? 0) > 0) markComplete('first_product');
      if ((profilesRes.data?.length ?? 0) > 1) markComplete('invite_team');
    };

    checkRealData();
  }, [agencyProfile.id]);

  const handleCTA = (step: ChecklistStep) => {
    markComplete(step.id);
    setCelebrating(step.id);
    setTimeout(() => {
      setCelebrating(null);
      navigate(step.ctaPath);
    }, 800);
  };

  const handleManualCheck = (stepId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    markComplete(stepId);
    setCelebrating(stepId);
    setTimeout(() => setCelebrating(null), 1000);
  };

  if (dismissed) return null;
  if (allDone) {
    return (
      <div
        className="rounded-2xl border p-5 flex items-center gap-4"
        style={{ borderColor: `${brand}30`, backgroundColor: `${brand}08` }}
      >
        <div
          className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0"
          style={{ backgroundColor: `${brand}20` }}
        >
          <i className="ri-trophy-line text-2xl" style={{ color: brand }} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-gray-800">Setup Complete!</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Your workspace is fully configured. You&apos;re ready to grow your agency.
          </p>
        </div>
        <button
          onClick={dismiss}
          className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap transition-colors px-3 py-1.5 rounded-lg hover:bg-white"
        >
          Dismiss
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div
        className="px-5 py-4 flex items-center gap-3 cursor-pointer select-none"
        onClick={() => setIsCollapsed((v) => !v)}
      >
        <div
          className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
          style={{ backgroundColor: `${brand}15` }}
        >
          <i className="ri-rocket-line text-lg" style={{ color: brand }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-gray-800">Get Started</p>
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${brand}15`, color: brand }}
            >
              {completedCount}/{totalCount}
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            Complete setup to unlock your agency&apos;s full potential
          </p>
        </div>

        {/* Progress ring */}
        <div className="relative w-10 h-10 flex-shrink-0">
          <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15" fill="none" stroke="#F3F4F6" strokeWidth="3" />
            <circle
              cx="18"
              cy="18"
              r="15"
              fill="none"
              stroke={brand}
              strokeWidth="3"
              strokeDasharray={`${(progressPct / 100) * 94.2} 94.2`}
              strokeLinecap="round"
              className="transition-all duration-700"
            />
          </svg>
          <span
            className="absolute inset-0 flex items-center justify-center text-[10px] font-bold"
            style={{ color: brand }}
          >
            {progressPct}%
          </span>
        </div>

        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
          {isCollapsed
            ? <i className="ri-arrow-down-s-line text-gray-400 text-lg" />
            : <i className="ri-arrow-up-s-line text-gray-400 text-lg" />
          }
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); dismiss(); }}
          className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors flex-shrink-0"
        >
          <i className="ri-close-line text-sm" />
        </button>
      </div>

      {/* Progress bar */}
      {!isCollapsed && (
        <div className="px-5 pb-1">
          <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${progressPct}%`, backgroundColor: brand }}
            />
          </div>
        </div>
      )}

      {/* Steps */}
      {!isCollapsed && (
        <div className="px-5 pb-5 pt-3 space-y-2">
          {STEPS.map((step, idx) => {
            const isDone = completed.has(step.id);
            const isCelebrating = celebrating === step.id;
            const isExpanded = expandedStep === step.id;
            const isLocked = idx > 0 && !completed.has(STEPS[idx - 1].id) && !isDone;

            return (
              <div
                key={step.id}
                className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                  isDone
                    ? 'border-gray-100 bg-gray-50/50'
                    : isExpanded
                    ? 'border-gray-200 bg-white shadow-sm'
                    : 'border-gray-100 bg-white hover:border-gray-200'
                } ${isLocked ? 'opacity-50' : 'cursor-pointer'}`}
                onClick={() => !isLocked && !isDone && setExpandedStep(isExpanded ? null : step.id)}
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  {/* Check circle */}
                  <button
                    onClick={(e) => !isDone && !isLocked && handleManualCheck(step.id, e)}
                    className={`w-6 h-6 flex items-center justify-center rounded-full border-2 flex-shrink-0 transition-all duration-300 ${
                      isDone
                        ? 'border-transparent'
                        : 'border-gray-300 hover:border-teal-400'
                    } ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    style={isDone ? { backgroundColor: brand, borderColor: brand } : {}}
                  >
                    {isCelebrating ? (
                      <i className="ri-check-line text-white text-xs animate-bounce" />
                    ) : isDone ? (
                      <i className="ri-check-line text-white text-xs" />
                    ) : null}
                  </button>

                  {/* Icon */}
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{
                      backgroundColor: isDone ? '#F3F4F6' : `${brand}12`,
                    }}
                  >
                    <i
                      className={`${step.icon} text-sm`}
                      style={{ color: isDone ? '#9CA3AF' : brand }}
                    />
                  </div>

                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm font-semibold leading-tight ${
                        isDone ? 'text-gray-400 line-through' : 'text-gray-800'
                      }`}
                    >
                      {step.title}
                    </p>
                    {isDone && (
                      <p className="text-xs text-green-500 mt-0.5 font-medium">
                        <i className="ri-check-double-line mr-1" />
                        {step.completedLabel}
                      </p>
                    )}
                  </div>

                  {/* Step number or arrow */}
                  {!isDone && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isLocked && (
                        <i className="ri-lock-line text-gray-300 text-sm" />
                      )}
                      {!isLocked && isExpanded && (
                        <i className="ri-arrow-up-s-line text-gray-400 text-base" />
                      )}
                      {!isLocked && !isExpanded && (
                        <i className="ri-arrow-right-s-line text-gray-400 text-base" />
                      )}
                    </div>
                  )}
                </div>

                {/* Expanded content */}
                {isExpanded && !isDone && (
                  <div className="px-4 pb-4 pt-0 border-t border-gray-50">
                    <p className="text-xs text-gray-500 leading-relaxed mb-3 mt-3">
                      {step.description}
                    </p>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleCTA(step); }}
                      className="flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-xl text-white cursor-pointer whitespace-nowrap transition-all hover:opacity-90 active:scale-95"
                      style={{ backgroundColor: brand }}
                    >
                      <i className={`${step.icon} text-sm`} />
                      {step.cta}
                      <i className="ri-arrow-right-line text-sm" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
